import { isEmpty, get } from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';
import {
  initialState as calendarInitialState,
  type CalendarState,
} from 'Store/calendar/calendar.reducer';
import { getDepartureDate } from 'Store/trip-criteria/tripCriteria.selector';
import { isBookable } from 'Utils/api-availability/availabilityUtils';
import { API_DATE_FORMAT, API_MONTH_ID } from 'Utils/date/apiDate';
const INITIAL_LIST = [];
const INITIAL_CALENDAR = {};

function getMonthIndex(months = [], monthId) {
  return months.findIndex(({ id }) => id === monthId);
}

function getMonthIndexFromDays(months = [], dayId) {
  /**
   * return if a day is elligible based on:
   * - dayContent.id equals dayId parameter
   * - dayContent.isCurrentMonth at true
   * @param  {Object} day  objet day from calendar.months[i]
   * @return {boolean}     this day is elligible
   */
  const matchBasedOnDay = ({ dayContent }) => dayContent.id === dayId && dayContent.isCurrentMonth;

  return months.findIndex(({ days }) => days.some(matchBasedOnDay));
}

function getCurrentMonth(months = [], selectedDay, selectedDayMonthIndex, firstMonthIndex) {
  return selectedDay && selectedDayMonthIndex > -1
    ? months[selectedDayMonthIndex]
    : months[firstMonthIndex];
}

function getDaysFromMonths(calendar, monthId) {
  const { months } = calendar;
  const monthIndex = getMonthIndex(months, monthId);
  return get(months, `${monthIndex}.days`);
}

function getDaysFromDays(calendar, dayId) {
  const { months, firstAvailableMonthIndex } = calendar;
  const selectedDayMonthIndex = getMonthIndexFromDays(months, dayId);
  const month = getCurrentMonth(months, dayId, selectedDayMonthIndex, firstAvailableMonthIndex);
  return get(month, 'days', INITIAL_LIST);
}

function getDaysSelector(calendar = INITIAL_CALENDAR, selectedMonth, selectedDay) {
  if (isEmpty(calendar)) {
    return INITIAL_LIST;
  }

  const daysFromMonths = getDaysFromMonths(calendar, selectedMonth);
  const daysFromDays = getDaysFromDays(calendar, selectedDay);
  return !daysFromMonths || daysFromMonths === daysFromDays ? daysFromDays : daysFromMonths;
}

const calendarSelector = (state: AppState): CalendarState => state.calendar ?? calendarInitialState;

export const getCalendar: (state: AppState) => CalendarState['calendar'] = createSelector(
  calendarSelector,
  ({ calendar }: CalendarState) => calendar,
);
export const getSelectedMonth: (state: AppState) => CalendarState['selectedMonth'] = createSelector(
  calendarSelector,
  ({ selectedMonth }: CalendarState) => selectedMonth,
);
export const getCalendarLoading: (state: AppState) => CalendarState['isLoading'] = createSelector(
  calendarSelector,
  ({ isLoading }: CalendarState) => isLoading,
);
export const getDays = createSelector(
  getCalendar,
  getSelectedMonth,
  getDepartureDate,
  getDaysSelector,
);
export const getPricesByDays: (state: AppState) => CalendarState['byDays'] = createSelector(
  calendarSelector,
  ({ byDays }: CalendarState) => byDays,
);
export const getDepartureDateBookable: (state: AppState) => boolean = createSelector(
  calendarSelector,
  getDepartureDate,
  getCalendarLoading,
  ({ byDays }: CalendarState, departureDate: number | null | undefined, isLoading: boolean) => {
    if (!departureDate || isLoading) {
      return false;
    }

    const dayId = moment(departureDate).format(API_DATE_FORMAT);
    return byDays && byDays[dayId] && isBookable(byDays[dayId]?.availability);
  },
);
export const getAvailableDurations: (state: AppState) => Array<number> = createSelector(
  calendarSelector,
  getDepartureDate,
  ({ bookableDates, bookableDatesLoading }, departureDate: number | null | undefined) => {
    if (bookableDatesLoading) {
      return [];
    }

    if (!departureDate || !bookableDates) {
      return null;
    }

    const monthId = moment(departureDate).format(API_MONTH_ID);
    const dayId = moment(departureDate).format(API_DATE_FORMAT);
    return Array.isArray(bookableDates?.months)
      ? bookableDates?.months
          .find((m) => m.id.toString() === monthId)
          ?.days.find((d) => d.id.toString() === dayId)
          ?.availabilities.filter((a) => a.status === 'AVAILABLE' || a.status === 'UNKNOWN')
          ?.reduce((acc, a) => [...acc, ...a.durations], []) || null
      : null;
  },
);
export const getInitialDuration: (state: AppState) => CalendarState['initialDuration'] =
  createSelector(calendarSelector, ({ initialDuration }: CalendarState) => initialDuration);
export const getCalendarTripPrice: (state: AppState) => CalendarState['tripPrice'] = createSelector(
  calendarSelector,
  ({ tripPrice }: CalendarState) => tripPrice,
);
export const getCalendarTripPriceLoading: (
  state: ReduxState,
) => CalendarState['tripPriceIsLoading'] = createSelector(
  calendarSelector,
  ({ tripPriceIsLoading }: CalendarState) => tripPriceIsLoading,
);
export const getBlockInputDepartureDate: (
  state: ReduxState,
) => CalendarState['blockInputDepartureDate'] = createSelector(
  calendarSelector,
  ({ blockInputDepartureDate }: CalendarState) => blockInputDepartureDate,
);
export const getCalendarDepartureCity: (state: AppState) => CalendarState['departureCity'] =
  createSelector(calendarSelector, ({ departureCity }: CalendarState) => departureCity);
export const getCalendarProductId: (state: AppState) => CalendarState['productId'] = createSelector(
  calendarSelector,
  ({ productId }: CalendarState) => productId,
);
export const getIsPriceCalendarSelection: (
  state: ReduxState,
) => CalendarState['isPriceCalendarSelection'] = createSelector(
  calendarSelector,
  ({ isPriceCalendarSelection }: CalendarState) => isPriceCalendarSelection,
);
